import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { STEPS } from "@/lib/collection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  {
    id: "homes",
    label: "Homes",
    text: "Hand-painted in your interior, in the colours and symbols of the artist you dream of. Measured, drawn and painted on site. A one-off piece, rooted in your space.",
  },
  {
    id: "institutions",
    label: "Art foundations & institutions",
    text: "On the vast wall of a museum or a foundation, the empty cross becomes a statement in its own right — a monumental invitation to imagine the masterpiece that isn't there yet.",
  },
  {
    id: "corporate",
    label: "Corporate offices",
    text: "For new spaces, fit-outs and empty walls. A gesture that turns an unfinished office into a statement of intent. Scalable across multiple walls and floors.",
  },
  {
    id: "hospitality",
    label: "Hospitality",
    text: "Hotels, restaurants, members' clubs. The X turns a lobby or a dining room into a talking point — a signature guests photograph and remember.",
  },
];

const FIELDS = [
  { name: "name", label: "Name", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "space_type", label: "Type of space", type: "text" },
  { name: "location", label: "City & country", type: "text" },
  { name: "wall_surface", label: "Approx. wall surface", type: "text" },
  { name: "subject", label: "Artist, object or word desired", type: "text" },
  { name: "timeline", label: "Timeline", type: "text" },
];

export default function Commission() {
  const [form, setForm] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/commissions`, form);
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#111111] min-h-screen text-[#faf8f5]">
      <Nav solid />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
              Commission
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-4xl font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              A real work, painted in your home —{" "}
              <span className="italic text-[#a89f8e]">not a poster, not a sticker.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base md:text-lg font-light leading-relaxed text-[#faf8f5]/70">
              Each “X” is created as an on-site commission: measured, drawn and
              painted by hand — the origin of the whole concept, and its most
              personal form.
            </p>
          </Reveal>

          {/* Audiences */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.06} className="border-t border-[#faf8f5]/15 pt-6">
                <h3 className="font-display text-xl md:text-2xl tracking-tight">{a.label}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#faf8f5]/65">
                  {a.text}
                </p>
              </Reveal>
            ))}
          </div>

          {/* Process */}
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.05} className="border-t border-[#faf8f5]/15 pt-6">
                <span className="font-display italic text-3xl text-[#a89f8e]">{s.n}</span>
                <h4 className="mt-3 font-display text-lg tracking-tight">{s.title}</h4>
                <p className="mt-2 text-sm font-light text-[#faf8f5]/60">{s.text}</p>
              </Reveal>
            ))}
          </div>

          {/* Enquiry form */}
          <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <h2 className="font-display text-2xl md:text-3xl tracking-tight">
                Request a commission
              </h2>
              <p className="mt-4 text-sm font-light text-[#faf8f5]/60">
                No prices, no checkout — tell us about your space and we'll reply
                with a proposal and a quote.
              </p>
            </div>

            <div className="lg:col-span-8">
              {sent ? (
                <div
                  data-testid="commission-success"
                  className="flex items-center gap-3 border border-[#faf8f5]/20 p-8"
                >
                  <Check className="h-5 w-5" strokeWidth={1.5} />
                  <span className="font-light">
                    Thank you — your enquiry has been received. We'll be in touch shortly.
                  </span>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="commission-form" className="space-y-6">
                  {/* Professional / Private toggle */}
                  <div className="flex gap-3" data-testid="commission-profile">
                    {[
                      { id: "private", label: "Private" },
                      { id: "professional", label: "Professional" },
                    ].map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => set("profile", p.id)}
                        data-testid={`commission-profile-${p.id}`}
                        className={`flex-1 border px-5 py-3 text-sm font-light transition-colors duration-300 ${
                          (form.profile || "private") === p.id
                            ? "border-[#faf8f5] bg-[#faf8f5] text-[#111111]"
                            : "border-[#faf8f5]/25 text-[#faf8f5]/70 hover:border-[#faf8f5]"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {FIELDS.map((f) => (
                      <div key={f.name}>
                        <label className="text-xs uppercase tracking-[0.2em] text-[#faf8f5]/50">
                          {f.label}
                          {f.required ? " *" : ""}
                        </label>
                        <input
                          type={f.type}
                          data-testid={`commission-${f.name}`}
                          value={form[f.name] || ""}
                          onChange={(e) => set(f.name, e.target.value)}
                          className="mt-2 w-full bg-transparent border-b border-[#faf8f5]/25 py-2 text-[#faf8f5] focus:outline-none focus:border-[#faf8f5]"
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#faf8f5]/50">
                      Message
                    </label>
                    <textarea
                      rows={4}
                      data-testid="commission-message"
                      value={form.message || ""}
                      onChange={(e) => set("message", e.target.value)}
                      className="mt-2 w-full bg-transparent border-b border-[#faf8f5]/25 py-2 text-[#faf8f5] focus:outline-none focus:border-[#faf8f5] resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="commission-submit"
                    className="bg-[#faf8f5] px-8 py-4 text-sm font-light tracking-wide text-[#111111] hover:opacity-85 transition-opacity duration-500 disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Send enquiry"}
                  </button>
                </form>
              )}
            </div>
          </div>
          {/* For professionals */}
          <div className="mt-24 border-t border-[#faf8f5]/15 pt-14" data-testid="comm-professionals">
            <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
              For professionals
            </span>
            <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-[#faf8f5]/60">
              Interior architects and designers: trade terms on request. Write to{" "}
              <a href="mailto:commissions@thexgoeshere.com" className="link-underline text-[#faf8f5]">
                commissions@thexgoeshere.com
              </a>
              .
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
